'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import WellnessCheckinForm from './components/WellnessCheckinForm';
import AthleteWellnessPanel from './components/AthleteWellnessPanel';
import { getTodayCheckin } from '@/lib/wellnessService';
import type { WellnessCheckinRow } from '@/lib/wellnessService';
import { ClipboardCheck, BarChart2, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


type Tab = 'checkin' | 'wellness';

export default function DailyWellnessPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('checkin');
  const [todayCheckin, setTodayCheckin] = useState<WellnessCheckinRow | null>(null);
  const [loadingToday, setLoadingToday] = useState(true);
  const [dateLabel, setDateLabel] = useState('');
  const [athleteId, setAthleteId] = useState<string | null>(null);
  const [athleteName, setAthleteName] = useState('');
  const [athleteSport, setAthleteSport] = useState('');

  useEffect(() => {
    setDateLabel(
      new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
    );
  }, []);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    supabase
      .from('athlete_profiles')
      .select('id, name, sport')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setAthleteId(data.id);
          setAthleteName(data.name ?? '');
          setAthleteSport(data.sport ?? '');
        }
      });
  }, [user]);

  useEffect(() => {
    if (!athleteId) {
      setLoadingToday(false);
      return;
    }
    getTodayCheckin(athleteId).then(({ data }) => {
      setTodayCheckin(data);
      setLoadingToday(false);
      if (data) setTab('wellness');
    });
  }, [athleteId]);

  const handleCheckinComplete = (_score: number, _status: string) => {
    if (!athleteId) return;
    getTodayCheckin(athleteId).then(({ data }) => {
      setTodayCheckin(data);
      setTab('wellness');
    });
  };

  const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'checkin', label: "Today's Check-In", icon: ClipboardCheck },
    { id: 'wellness', label: 'My Wellness', icon: BarChart2 },
  ];

  return (
    <AppLayout activePath="/daily-wellness">
      <div className="space-y-6 max-w-2xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="page-title">Daily Wellness Check-In</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {athleteName ? `${athleteName}${athleteSport ? ` · ${athleteSport}` : ''}` : 'Complete your profile to get started'}
            {dateLabel ? ` · ${dateLabel}` : ''}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-zinc-900 border border-zinc-800 rounded-xl">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-150"
              style={tab === id ? {
                background: 'rgba(225,6,0,0.1)',
                color: '#E10600',
                border: '1px solid rgba(225,6,0,0.2)',
              } : {
                color: '#71717a',
                border: '1px solid transparent',
              }}
            >
              <Icon size={15} />
              {label}
              {id === 'checkin' && todayCheckin && (
                <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" title="Completed today" />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loadingToday ? (
          <div className="card p-12 flex items-center justify-center">
            <Loader2 size={24} className="animate-spin text-zinc-500" />
          </div>
        ) : !athleteId ? (
          <div
            className="card p-10 text-center"
          >
            <p className="text-sm text-zinc-400 mb-2">No athlete profile found.</p>
            <p className="text-xs text-zinc-600">Complete your profile setup to use the wellness check-in.</p>
          </div>
        ) : tab === 'checkin' ? (
          <WellnessCheckinForm
            athleteId={athleteId}
            userId={user?.id ?? null}
            onComplete={handleCheckinComplete}
          />
        ) : (
          <AthleteWellnessPanel
            athleteId={athleteId}
            todayCheckin={todayCheckin}
          />
        )}
      </div>
    </AppLayout>
  );
}
